import { useMemo, useState } from 'react'
import { useAuth } from '@clerk/clerk-react'
import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import { Card } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { formatRelative } from '@/lib/dates'

type Tenant = {
  id: string
  name: string
  city: string | null
  province: string | null
  clerk_org_id: string | null
  owner_email: string | null
  member_count: number
  suppliers: Array<{ supplier: string; connected: boolean }>
  last_activity_at: string | null
  provisioned_at: string | null
  suspended_at: string | null
  assistant_name: string | null
  status: 'active' | 'pending_invite' | 'suspended'
}

type Resp = { tenants: Tenant[] }

/**
 * Admin landing page — tenant directory.
 *
 * Lists every provisioned shop with provisioning status, supplier
 * connections, member count, and last activity. The "+ Invite shop"
 * button kicks off the curated onboarding wizard.
 *
 * Gated by `<RequirePlatformAdmin>` in App.tsx; the Worker also gates
 * /api/admin/tenants server-side via `requirePlatformAdmin()`.
 */
export function AdminTenants() {
  const { t } = useTranslation('admin')
  const { t: tCommon } = useTranslation('common')
  const { getToken, isLoaded } = useAuth()
  const [search, setSearch] = useState('')

  const query = useQuery<Resp>({
    queryKey: ['admin', 'tenants'],
    enabled: isLoaded,
    queryFn: async () => {
      const token = await getToken()
      if (!token) throw new Error(tCommon('error.missing_token'))
      const res = await fetch('/api/admin/tenants', {
        headers: { Authorization: `Bearer ${token}` },
      })
      if (!res.ok) {
        throw new Error(
          tCommon('error.request_failed', { status: res.status }),
        )
      }
      return (await res.json()) as Resp
    },
  })

  const tenants = useMemo(() => query.data?.tenants ?? [], [query.data])
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return tenants
    return tenants.filter((s) => {
      return (
        s.name.toLowerCase().includes(q) ||
        s.owner_email?.toLowerCase().includes(q) ||
        s.clerk_org_id?.toLowerCase().includes(q)
      )
    })
  }, [tenants, search])

  const counts = useMemo(() => {
    const active = tenants.filter((s) => s.status === 'active').length
    const pending = tenants.filter((s) => s.status === 'pending_invite').length
    const suspended = tenants.filter((s) => s.status === 'suspended').length
    return { active, pending, suspended }
  }, [tenants])

  return (
    <div className="space-y-5">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-foreground">
            {t('tenants.title')}
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {t('tenants.subtitle', {
              count: tenants.length,
              active: counts.active,
              pending: counts.pending,
              suspended: counts.suspended,
            })}
          </p>
        </div>
        <Button asChild>
          <Link to="/tenants/new">+ {t('tenants.invite_shop')}</Link>
        </Button>
      </div>

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={t('tenants.search_placeholder')}
          className="sm:max-w-md"
        />
        <div className="ml-auto text-xs text-muted-foreground">
          {t('tenants.showing', {
            count: filtered.length,
            total: tenants.length,
          })}
        </div>
      </div>

      {query.isError ? (
        <Card className="border-destructive/30 bg-destructive/10 p-4 text-sm text-destructive">
          {query.error instanceof Error
            ? query.error.message
            : t('tenants.error')}
        </Card>
      ) : null}

      {query.isLoading ? (
        <Card className="p-4 text-sm text-muted-foreground">
          {tCommon('loading')}
        </Card>
      ) : null}

      {query.data && filtered.length === 0 ? (
        <Card className="p-8 text-center text-sm text-muted-foreground">
          {t('tenants.empty')}
        </Card>
      ) : null}

      {query.data && filtered.length > 0 ? (
        <Card className="overflow-hidden p-0">
          {/* Desktop table */}
          <div className="hidden md:block">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border bg-muted/40 text-left text-xs uppercase tracking-wide text-muted-foreground">
                  <th className="px-4 py-3 font-semibold">
                    {t('tenants.table.shop')}
                  </th>
                  <th className="px-4 py-3 font-semibold">
                    {t('tenants.table.owner')}
                  </th>
                  <th className="px-4 py-3 font-semibold">
                    {t('tenants.table.members')}
                  </th>
                  <th className="px-4 py-3 font-semibold">
                    {t('tenants.table.suppliers')}
                  </th>
                  <th className="px-4 py-3 font-semibold">
                    {t('tenants.table.status')}
                  </th>
                  <th className="px-4 py-3 font-semibold">
                    {t('tenants.table.last_activity')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((s) => (
                  <tr
                    key={s.id}
                    className="border-b border-border/60 last:border-0 hover:bg-muted/20"
                  >
                    <td className="px-4 py-3">
                      <Link
                        to={`/tenants/${s.id}`}
                        className="block min-h-11 hover:text-brand"
                      >
                        <div className="font-semibold text-foreground">
                          {s.name}
                        </div>
                        <div className="text-[11px] text-muted-foreground">
                          {[s.city, s.province].filter(Boolean).join(', ') ||
                            '—'}
                        </div>
                      </Link>
                    </td>
                    <td className="px-4 py-3 text-foreground">
                      {s.owner_email ?? '—'}
                    </td>
                    <td className="px-4 py-3 text-foreground">
                      {s.member_count}
                    </td>
                    <td className="px-4 py-3">
                      <SupplierChips suppliers={s.suppliers} />
                    </td>
                    <td className="px-4 py-3">
                      <StatusChip status={s.status} />
                    </td>
                    <td className="px-4 py-3 text-xs text-muted-foreground">
                      {s.last_activity_at
                        ? formatRelative(s.last_activity_at)
                        : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {/* Mobile cards */}
          <ul className="divide-y divide-border md:hidden">
            {filtered.map((s) => (
              <li key={s.id}>
                <Link
                  to={`/tenants/${s.id}`}
                  className="block px-4 py-4 hover:bg-muted/20"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="truncate font-semibold text-foreground">
                        {s.name}
                      </div>
                      <div className="truncate text-xs text-muted-foreground">
                        {s.owner_email ?? '—'}
                      </div>
                    </div>
                    <StatusChip status={s.status} />
                  </div>
                  <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
                    <span>
                      {s.member_count} {t('tenant_detail.kpis.members')}
                    </span>
                    <SupplierChips suppliers={s.suppliers} />
                    <span className="ml-auto">
                      {s.last_activity_at
                        ? formatRelative(s.last_activity_at)
                        : '—'}
                    </span>
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        </Card>
      ) : null}
    </div>
  )
}

function StatusChip({
  status,
}: {
  status: 'active' | 'pending_invite' | 'suspended'
}) {
  const { t } = useTranslation('admin')
  if (status === 'active') {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-semibold text-emerald-800">
        <span className="size-1.5 rounded-full bg-emerald-600" />
        {t('tenants.status.active')}
      </span>
    )
  }
  if (status === 'pending_invite') {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-semibold text-amber-800">
        <span className="size-1.5 rounded-full bg-amber-500" />
        {t('tenants.status.pending_invite')}
      </span>
    )
  }
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-rose-100 px-2 py-0.5 text-[11px] font-semibold text-rose-800">
      <span className="size-1.5 rounded-full bg-rose-500" />
      {t('tenants.status.suspended')}
    </span>
  )
}

function SupplierChips({
  suppliers,
}: {
  suppliers: Array<{ supplier: string; connected: boolean }>
}) {
  const { t } = useTranslation('admin')
  const connected = suppliers.filter((s) => s.connected)
  if (connected.length === 0) {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-[11px] font-semibold text-muted-foreground">
        <span className="size-1.5 rounded-full bg-muted-foreground/60" />
        {t('tenants.supplier_status.none')}
      </span>
    )
  }
  return (
    <span className="flex flex-wrap gap-1.5">
      {connected.map((s) => (
        <span
          key={s.supplier}
          className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-semibold text-emerald-800"
        >
          <span className="size-1.5 rounded-full bg-emerald-600" />
          {t('tenants.supplier_status.connected', { supplier: s.supplier })}
        </span>
      ))}
    </span>
  )
}
